import { useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "./components/AppShell";
import { FeeSettingsPage } from "./components/FeeSettingsPage";
import { KycPage } from "./components/KycPage";
import { LoginPage } from "./components/LoginPage";
import { AuditPage } from "./components/AuditPage";
import { CardTemplatesPage } from "./components/CardTemplatesPage";
import { CardsPage } from "./components/CardsPage";
import { CrashesPage } from "./components/CrashesPage";
import { LogsPage } from "./components/LogsPage";
import { TransactionsPage } from "./components/TransactionsPage";
import { UsersPage } from "./components/UsersPage";
import { ControlsPage } from "./components/ControlsPage";
import { StaffPage } from "./components/StaffPage";
import { NotificationsPage } from "./components/NotificationsPage";
import { OverviewPage } from "./components/OverviewPage";
import { CostsPage } from "./components/CostsPage";
import { CustomerPage } from "./components/CustomerPage";
import { TicketsPage } from "./components/TicketsPage";
import { TicketEmailSettingsPage } from "./components/TicketEmailSettingsPage";
import { endSession, startGoogleSession } from "./lib/auth";
import { getViewer } from "./lib/api";
import { currentRoute, isSameRoute, routeToPath, type Route } from "./lib/routing";
import { routeForRole } from "./lib/roles";
import type { AdminUserSummary, PortalView, Viewer } from "./types";

/** Views a customer profile can be opened from, so "back" returns where you came from. */
const CUSTOMER_ORIGINS: PortalView[] = ["users", "kyc", "transactions"];

export default function App() {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [checking, setChecking] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState<Route>(() => currentRoute());
  const [selectedCustomer, setSelectedCustomer] = useState<AdminUserSummary | null>(null);
  const [customerOrigin, setCustomerOrigin] = useState<PortalView>("users");
  const [notificationRecipient, setNotificationRecipient] = useState<string | null>(null);

  // Mirrors `route` so navigation can compare without reading it inside a state
  // updater — StrictMode invokes updaters twice, which would double-push history.
  const routeRef = useRef(route);

  useEffect(() => {
    const onPopState = () => {
      const requested = currentRoute();
      const next = routeForRole(requested, viewer?.role);
      if (!isSameRoute(requested, next)) window.history.replaceState(null, "", routeToPath(next));
      if (!isSameRoute(routeRef.current, next) && !window.dispatchEvent(new Event("ottagive:before-navigation", { cancelable: true }))) { window.history.pushState(null, "", routeToPath(routeRef.current)); return; }
      routeRef.current = next;
      setRoute(next);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [viewer?.role]);

  useEffect(() => {
    const next = routeForRole(routeRef.current, viewer?.role);
    if (!isSameRoute(routeRef.current, next)) {
      window.history.replaceState(null, "", routeToPath(next));
      routeRef.current = next;
      setRoute(next);
    }
  }, [viewer?.role]);

  const go = useCallback((next: Route) => {
    next = routeForRole(next, viewer?.role);
    if (isSameRoute(routeRef.current, next)) return;
    if (!window.dispatchEvent(new Event("ottagive:before-navigation", { cancelable: true }))) return;
    routeRef.current = next;
    window.history.pushState(null, "", routeToPath(next));
    window.scrollTo({ top: 0 });
    setRoute(next);
  }, [viewer?.role]);

  const navigate = useCallback((view: PortalView) => go({ view, customerGuid: null }), [go]);

  const openCustomer = useCallback((user: AdminUserSummary) => {
    setSelectedCustomer(user);
    setCustomerOrigin((current) => (CUSTOMER_ORIGINS.includes(route.view) ? route.view : current));
    go({ view: "customer", customerGuid: user.customerGuid });
  }, [go, route.view]);

  const notifyCustomer = useCallback((user: AdminUserSummary) => {
    setNotificationRecipient(user.firebaseUid);
    navigate("notifications");
  }, [navigate]);

  const refreshViewer = async () => {
    setChecking(true);
    try {
      setViewer(await getViewer());
    } catch (sessionError) {
      setError(sessionError instanceof Error ? sessionError.message : "Unable to verify your session");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    void refreshViewer();
  }, []);

  const login = async () => {
    setAuthenticating(true);
    setError(null);
    try {
      await startGoogleSession();
      setChecking(true);
      const authenticatedViewer = await getViewer();
      if (!authenticatedViewer) {
        throw new Error("Google sign-in succeeded, but the secure portal session was not accepted. Please try once more.");
      }
      setViewer(authenticatedViewer);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Sign-in failed");
    } finally {
      setChecking(false);
      setAuthenticating(false);
    }
  };

  const logout = async () => {
    if (!window.dispatchEvent(new Event("ottagive:before-navigation", { cancelable: true }))) return;
    await endSession();
    setViewer(null);
  };

  if (checking) {
    return <main className="app-loading"><span className="brand-mark">O</span><p>Verifying secure session…</p></main>;
  }

  if (!viewer) return <LoginPage error={error} loading={authenticating} onSignIn={() => void login()} />;

  // Never mount a forbidden component, even before the URL-correction effect runs.
  const { view } = routeForRole(route, viewer.role);

  return (
    <AppShell viewer={viewer} activeView={view} onNavigate={navigate} onLogout={() => void logout()}>
      {view === "overview" ? <OverviewPage viewer={viewer} onNavigate={navigate} /> : null}
      {view === "tickets" ? <TicketsPage viewer={viewer} /> : null}
      {view === "ticket-email-settings" ? <TicketEmailSettingsPage viewer={viewer} /> : null}
      {view === "users" ? <UsersPage viewer={viewer} onOpenCustomer={openCustomer} /> : null}
      {view === "customer" ? <CustomerPage customerGuid={route.customerGuid} initialUser={selectedCustomer} viewer={viewer} onNavigate={navigate} backTo={customerOrigin} /> : null}
      {view === "kyc" ? <KycPage viewer={viewer} onOpenCustomer={openCustomer} onNotifyCustomer={notifyCustomer} /> : null}
      {view === "transactions" ? <TransactionsPage viewer={viewer} onOpenCustomer={openCustomer} /> : null}
      {view === "cards" ? <CardsPage onNavigate={navigate} /> : null}
      {view === "legacy-cards" ? <CardsPage mode="legacy" onNavigate={navigate} /> : null}
      {view === "crashes" ? <CrashesPage /> : null}
      {view === "card-templates" ? <CardTemplatesPage viewer={viewer} /> : null}
      {view === "logs" ? <LogsPage /> : null}
      {view === "costs" ? <CostsPage /> : null}
      {view === "audit" ? <AuditPage /> : null}
      {view === "notifications" ? <NotificationsPage viewer={viewer} initialRecipientUid={notificationRecipient} initialTemplateId={notificationRecipient ? "kyc_action_required" : undefined} /> : null}
      {view === "controls" ? <ControlsPage viewer={viewer} /> : null}
      {view === "staff" ? <StaffPage viewer={viewer} /> : null}
      {view === "settings" ? <FeeSettingsPage viewer={viewer} /> : null}
    </AppShell>
  );
}
